import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface AdminPermissionsContextType {
  permissions: string[];
  isSuperAdmin: boolean;
  isStaff: boolean;
  loading: boolean;
  hasPermission: (slug: string) => boolean;
  hasAnyPermission: (slugs: string[]) => boolean;
  checkScope: (scopeType: string, scopeValue: string) => boolean;
  scopes: { scope_type: string; scope_value: string }[];
  refetch: () => Promise<void>;
}

const AdminPermissionsContext = createContext<AdminPermissionsContextType>({
  permissions: [],
  isSuperAdmin: false,
  isStaff: false,
  loading: true,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  checkScope: () => true,
  scopes: [],
  refetch: async () => {},
});

export function AdminPermissionsProvider({ children }: { children: ReactNode }) {
  const { user, isAdmin } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [scopes, setScopes] = useState<{ scope_type: string; scope_value: string }[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user?.id) {
      setPermissions([]);
      setIsSuperAdmin(false);
      setIsStaff(false);
      setScopes([]);
      setLoading(false);
      return;
    }

    try {
      // 1. Get user's admin roles
      const { data: userRoles } = await supabase
        .from("admin_user_roles")
        .select("role_id, status, expires_at, admin_roles!inner(slug, is_system, status)")
        .eq("user_id", user.id)
        .eq("status", "active");

      const now = Date.now();
      const currentRoles = (userRoles || []).filter(
        (ur: any) => !ur.expires_at || new Date(ur.expires_at).getTime() > now
      );
      setIsStaff(isAdmin || currentRoles.length > 0);

      if (currentRoles.length === 0) {
        // No RBAC roles assigned — legacy admin, treat as super admin for backward compat
        setIsSuperAdmin(isAdmin);
        setPermissions([]);
        setScopes([]);
        setLoading(false);
        return;
      }

      const activeRoles = currentRoles.filter(
        (ur: any) => ur.admin_roles?.status === "active"
      );
      const hasSuperAdmin = activeRoles.some((ur: any) => ur.admin_roles?.is_system);

      if (hasSuperAdmin) {
        setIsSuperAdmin(true);
        setPermissions([]);
        setScopes([]);
        setLoading(false);
        return;
      }

      // 2. Get permissions for all active roles
      const roleIds = activeRoles.map((ur: any) => ur.role_id);
      const { data: rolePerms } = await supabase
        .from("admin_role_permissions")
        .select("admin_permissions!inner(slug)")
        .in("role_id", roleIds);

      const slugs = new Set(
        (rolePerms || []).map((rp: any) => rp.admin_permissions?.slug).filter(Boolean)
      );

      // 3. Merge temporary access grants
      const { data: tempGrants } = await supabase
        .from("temporary_access_grants")
        .select("permission_slug")
        .eq("user_id", user.id)
        .eq("status", "active")
        .lte("valid_from", new Date().toISOString())
        .gt("valid_until", new Date().toISOString())
        .is("revoked_at", null);

      (tempGrants || []).forEach((g: any) => {
        if (g.permission_slug) slugs.add(g.permission_slug);
      });

      // 4. Merge delegation permissions
      const { data: delegations } = await supabase
        .from("delegation_rules")
        .select("delegator_id, permissions")
        .eq("delegate_id", user.id)
        .eq("status", "active")
        .lte("valid_from", new Date().toISOString())
        .gt("valid_until", new Date().toISOString())
        .is("revoked_at", null);

      if (delegations && delegations.length > 0) {
        // For each delegation, get the delegator's permissions
        const delegatorIds = delegations.map((d: any) => d.delegator_id);
        const { data: delegatorRoles } = await supabase
          .from("admin_user_roles")
          .select("role_id")
          .in("user_id", delegatorIds);

        if (delegatorRoles && delegatorRoles.length > 0) {
          const delegatorRoleIds = delegatorRoles.map((r: any) => r.role_id);
          const { data: delegatorPerms } = await supabase
            .from("admin_role_permissions")
            .select("admin_permissions!inner(slug)")
            .in("role_id", delegatorRoleIds);

          (delegatorPerms || []).forEach((rp: any) => {
            if (rp.admin_permissions?.slug) slugs.add(rp.admin_permissions.slug);
          });
        }
      }

      // 5. Fetch staff scopes
      const { data: staffScopes } = await supabase
        .from("staff_scopes")
        .select("scope_type, scope_value")
        .eq("user_id", user.id)
        .eq("enabled", true);

      setIsSuperAdmin(false);
      setPermissions([...slugs] as string[]);
      setScopes(staffScopes || []);
    } catch (err) {
      console.error("Failed to fetch admin permissions:", err);
      setPermissions([]);
      setIsSuperAdmin(false);
      setIsStaff(false);
      setScopes([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdmin]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback(
    (slug: string) => isSuperAdmin || permissions.includes(slug),
    [isSuperAdmin, permissions]
  );

  const hasAnyPermission = useCallback(
    (slugs: string[]) => isSuperAdmin || slugs.some((s) => permissions.includes(s)),
    [isSuperAdmin, permissions]
  );

  const checkScope = useCallback(
    (scopeType: string, scopeValue: string) => {
      if (isSuperAdmin) return true;
      const relevantScopes = scopes.filter((s) => s.scope_type === scopeType);
      if (relevantScopes.length === 0) return true; // no scopes = unrestricted
      return relevantScopes.some((s) => s.scope_value === scopeValue);
    },
    [isSuperAdmin, scopes]
  );

  return (
    <AdminPermissionsContext.Provider
      value={{ permissions, isSuperAdmin, isStaff, loading, hasPermission, hasAnyPermission, checkScope, scopes, refetch: fetchPermissions }}
    >
      {children}
    </AdminPermissionsContext.Provider>
  );
}

export const useAdminPermissions = () => useContext(AdminPermissionsContext);
